<template>
    <div>
        <div class="flex items-center justify-between">
            <label
                v-if="label"
                role="label"
                :for="id"
                class="relative pb-[3px] pr-3 text-sm font-medium leading-normal text-gray-700"
                :class="{ 'text-gray-400': disabled }"
            >
                {{ label }}
                <img
                    v-if="required"
                    class="absolute right-0 top-0 h-2 w-2"
                    src="https://d5aoblv5p04cg.cloudfront.net/application/images/components/star-required.svg"
                    alt="Required star illustration icon textarea input"
                />
            </label>

            <span
                v-if="secondarylabel"
                class="pb-[3px] text-sm leading-normal text-gray-500"
                >{{ secondarylabel }}</span
            >

            <span
                v-if="headeraction"
                class="pb-[3px] text-sm font-medium leading-normal"
                :class="{
                    'text-gray-400 ': disabled,
                    'cursor-pointer text-custom-blue-500 hover:text-custom-blue-600':
                        !disabled,
                }"
                @click="!disabled ? $emit('headeraction') : null"
                >{{ headeraction }}</span
            >
        </div>

        <div class="relative">
            <textarea
                :id="id"
                :value="modelValue"
                :required="required"
                :placeholder="placeholder"
                :disabled="disabled"
                :rows="rows"
                :cols="cols"
                class="block w-full rounded-md border bg-white px-3 py-2 text-sm shadow-button transition duration-100 ease-in-out focus:outline-hidden disabled:cursor-not-allowed disabled:text-gray-300 disabled:placeholder-gray-300 disabled:shadow-none disabled:hover:bg-white"
                :class="{
                    'border-custom-alert-100 text-custom-alert-100 placeholder-custom-red-300 ':
                        error,
                    'border-gray-300 text-gray-600 placeholder-gray-400 hover:bg-gray-50 focus:border-blue-400 focus:text-gray-800':
                        !error,
                }"
                @input="onInput"
                @blur="$emit('blur')"
            />

            <div
                v-if="hasHelpSlot && !error"
                class="absolute mt-[3px] text-xs leading-5"
                :class="{
                    'text-gray-400': disabled,
                    'text-gray-500': !disabled,
                }"
            >
                <slot name="help" />
            </div>

            <div
                v-if="error"
                class="absolute mt-[3px] flex items-center text-custom-alert-100"
            >
                <svg
                    class="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                    />
                </svg>

                <div class="ml-2 text-xs leading-5">
                    {{ error }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

defineProps({
    error: {
        type: String,
        default: "",
    },
    label: {
        type: String,
        default: "",
    },
    secondarylabel: {
        type: String,
        default: "",
    },
    rows: {
        type: Number,
        default: null,
    },
    cols: {
        type: Number,
        default: null,
    },
    required: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    resize: {
        type: Boolean,
        default: false,
    },
    modelValue: {
        type: String,
        required: true,
    },
    placeholder: {
        type: String,
        default: "",
    },
    headeraction: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["update:modelValue", "headeraction", "blur-sm"]);

const slots = useSlots();
const id = Math.floor(Math.random() * 100);

const onInput = (e: Event) => {
    const inputEvent = e.target as HTMLInputElement;

    if (inputEvent.value !== null) {
        emit("update:modelValue", inputEvent.value);
    }
};

const hasHelpSlot = computed(() => {
    return !!slots.help;
});
</script>

<style></style>
